import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { BookOpen } from "lucide-react";
import type { ReactElement } from "react";
import { AuthRequired } from "../../components/AuthRequired";
import { StoryEditor } from "../../components/StoryEditor";
import { StoryLoadingState } from "../../components/StoryLoadingState";
import { StoryViewerPage } from "../../components/StoryViewerPage";
import { CenteredCard } from "../../components/ui/centered-card";
import { useAuth } from "../../contexts/AuthContext";
import { apiClient } from "../../lib/api";

export const Route = createFileRoute("/_layout/story/$storyId")({
    component: StoryRoute,
});

function StoryRoute(): ReactElement {
    const { storyId } = Route.useParams();
    const { user, loading } = useAuth();

    // Fetch story data once
    const {
        data: storyData,
        isLoading: isLoadingStory,
        error: storyError,
    } = useQuery({
        queryKey: ["story", storyId],
        queryFn: () => apiClient.getStory(storyId),
        enabled: !!user && !!storyId,
        staleTime: 30 * 1000, // 30 second cache
        retry: 1,
    });

    // Show loading state while auth is being determined
    if (loading) {
        return <StoryLoadingState />;
    }

    if (!user) {
        return (
            <AuthRequired
                icon={<BookOpen className="h-12 w-12 text-primary mx-auto" />}
                description="Please sign in to continue your story!"
            />
        );
    }

    if (isLoadingStory) {
        return <StoryLoadingState />;
    }

    if (storyError || !storyData) {
        return (
            <CenteredCard>
                <div className="text-center space-y-4">
                    <BookOpen className="h-12 w-12 text-destructive mx-auto" />
                    <h2 className="text-2xl font-bold">Story Not Found</h2>
                    <p className="text-muted-foreground">This story doesn't exist or you don't have access to it.</p>
                </div>
            </CenteredCard>
        );
    }

    return storyData.story.status === "finalized" ? (
        <StoryViewerPage storyData={storyData} />
    ) : (
        <StoryEditor storyData={storyData} />
    );
}
